import type { RecommendationItem, RecommendationRequest } from './index.h'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { parameters as playStartParameters } from '~/features/analytix/player/parameters/play-start'
import { parameters as playerOpenParameters } from '~/features/analytix/player/parameters/player-open'
import { playerRecommendationsShown } from '~/features/analytix/ui/models/player-recommendations'
import { profilesFeaturesModel } from '~/features/profiles'
import { model as config, remote } from '~/shared/config'
import { mapShowItem, mapVodItem } from './lib/loadRecommendations'

export const PLAYER_RECOMMENDATIONS_COUNT = 4

export const $isRecommendedRowsEnabled = config.get(
  remote.tria_isRecommendedRowsEnabled
)

export const requestRecommendations = createEvent<RecommendationRequest>()
export const closeRecommendations = createEvent()
export const resetRecommendations = createEvent()

const loadVodRecommendationsFx = attach({
  effect: api.vods.getPlayerRecommendedVodsFx,
  mapParams: (contentId: number) => ({
    vodId: contentId,
    count: PLAYER_RECOMMENDATIONS_COUNT,
  }),
})

const loadShowRecommendationsFx = attach({
  effect: api.tvshows.getPlayerRecommendedTvShowsFx,
  mapParams: (contentId: number) => ({
    tvShowId: contentId,
    count: PLAYER_RECOMMENDATIONS_COUNT,
  }),
})

const recommendationsLoaded = createEvent<RecommendationItem[]>()

const runFallbackFx = createEffect((fallback: (() => void) | null) => {
  fallback?.()
})

const loadRecommendationsRequested =
  createEvent<Pick<RecommendationRequest, 'sourceType' | 'contentId'>>()

const $pendingRecommendationRequest = createStore<Pick<
  RecommendationRequest,
  'sourceType' | 'contentId'
> | null>(null)
  .on(loadRecommendationsRequested, (_, request) => request)
  .reset(resetRecommendations)

export const $recommendationItems = createStore<RecommendationItem[]>([])
  .on(recommendationsLoaded, (_, items) => items)
  .on(closeRecommendations, () => [])
  .on(resetRecommendations, () => [])

export const $isRecommendationsLoading = combine(
  loadVodRecommendationsFx.pending,
  loadShowRecommendationsFx.pending,
  (vodPending, showPending) => vodPending || showPending
)

export const $isRecommendationsActive = combine(
  $isRecommendationsLoading,
  $recommendationItems,
  $isRecommendedRowsEnabled,
  (loading, items, enabled) => enabled && (loading || items.length > 0)
)

const $pendingFallback = createStore<(() => void) | null>(null)
  .on(requestRecommendations, (_, { fallback }) => fallback)
  .reset([
    loadVodRecommendationsFx.done,
    loadVodRecommendationsFx.fail,
    loadShowRecommendationsFx.done,
    loadShowRecommendationsFx.fail,
    resetRecommendations,
  ])

sample({
  clock: requestRecommendations,
  source: {
    isRegular: profilesFeaturesModel.$isActiveProfileRegular,
    isEnabled: $isRecommendedRowsEnabled,
  },
  filter: ({ isRegular, isEnabled }) => !isEnabled || !isRegular,
  fn: (_, request) => request.fallback,
  target: runFallbackFx,
})

sample({
  clock: requestRecommendations,
  source: {
    isRegular: profilesFeaturesModel.$isActiveProfileRegular,
    isEnabled: $isRecommendedRowsEnabled,
  },
  filter: ({ isRegular, isEnabled }) => isEnabled && isRegular,
  fn: (_, request) => ({
    sourceType: request.sourceType,
    contentId: request.contentId,
  }),
  target: loadRecommendationsRequested,
})

sample({
  clock: loadRecommendationsRequested,
  filter: ({ sourceType }) => sourceType === 'vod',
  fn: ({ contentId }) => contentId,
  target: loadVodRecommendationsFx,
})

sample({
  clock: loadRecommendationsRequested,
  filter: ({ sourceType }) => sourceType === 'show',
  fn: ({ contentId }) => contentId,
  target: loadShowRecommendationsFx,
})

sample({
  clock: loadVodRecommendationsFx.doneData,
  fn: (page) => page.content.map(mapVodItem),
  target: recommendationsLoaded,
})

sample({
  clock: loadShowRecommendationsFx.doneData,
  fn: (shows) => shows.map(mapShowItem),
  target: recommendationsLoaded,
})

sample({
  clock: loadVodRecommendationsFx.doneData,
  source: $pendingFallback,
  filter: (_, page) => page.content.length === 0,
  fn: (fallback) => fallback,
  target: runFallbackFx,
})

sample({
  clock: loadShowRecommendationsFx.doneData,
  source: $pendingFallback,
  filter: (_, shows) => shows.length === 0,
  fn: (fallback) => fallback,
  target: runFallbackFx,
})

sample({
  clock: [loadVodRecommendationsFx.fail, loadShowRecommendationsFx.fail],
  source: $pendingFallback,
  target: runFallbackFx,
})

sample({
  clock: recommendationsLoaded,
  source: $pendingRecommendationRequest,
  filter: (_, items) => items.length > 0,
  fn: (request) => ({
    contentId:
      playStartParameters.get('contentId') ??
      playerOpenParameters.get('contentId') ??
      (request ? String(request.contentId) : undefined),
    contentType:
      playStartParameters.get('contentType') ??
      playerOpenParameters.get('contentType') ??
      (request?.sourceType === 'show' ? 'tvshow' : 'vod'),
    contentName:
      playStartParameters.get('contentName') ??
      playerOpenParameters.get('contentName'),
    programName:
      playStartParameters.get('programName') ??
      playerOpenParameters.get('programName') ??
      '',
  }),
  target: playerRecommendationsShown,
})
